import { useEffect, useId, useState } from 'react';
import { Button } from '../atoms/Button/Button';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Text } from '../atoms/Typography/Text';
import { useTranslation } from '../../i18n/useTranslation';
import './TranscriptPanel.css';

export interface TranscriptPanelProps {
  transcript: string;
  /** Span [start, end) of the focused suggestion, highlighted in the transcript. */
  highlight?: { start: number; end: number } | null;
  /** Credits the next re-analysis costs (0 = included). */
  reanalysisCredits: number;
  onReanalyse: (transcript: string) => void;
  isReanalysing?: boolean;
  /** Opens the panel initially (e.g. while a suggestion needs checking). */
  defaultOpen?: boolean;
  disabled?: boolean;
  /** A "Text only" note: the button runs the first AI analysis ("Analyse with AI"). */
  upgrade?: boolean;
}

/**
 * "Show what you said": the transcript as an editable text field with
 * Re-analyse (misheard words are fixed here, M1), and above it a read-only copy
 * with the phrase of the focused suggestion marked — background plus
 * underline, never colour alone.
 */
export function TranscriptPanel({
  transcript,
  highlight = null,
  reanalysisCredits,
  onReanalyse,
  isReanalysing = false,
  defaultOpen = false,
  disabled = false,
  upgrade = false,
}: TranscriptPanelProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const [open, setOpen] = useState(defaultOpen);
  const [draft, setDraft] = useState(transcript);

  // A new transcript (re-analysis, another user edit saved) replaces the draft.
  useEffect(() => {
    setDraft(transcript);
  }, [transcript]);

  const span =
    highlight && highlight.start >= 0 && highlight.end > highlight.start && highlight.end <= transcript.length && draft === transcript
      ? highlight
      : null;

  return (
    <div className="transcript-panel">
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? t('voice.card.hideTranscript') : t('voice.card.showTranscript')}
      </Button>
      <div id={panelId} hidden={!open} className="transcript-panel__body">
        {span && (
          <div className="transcript-panel__preview-wrap">
            <Text variant="caption" color="muted">{t('voice.card.transcriptHighlightLabel')}</Text>
            <Text as="div" size="sm" className="transcript-panel__preview" data-testid="transcript-preview">
              {transcript.slice(0, span.start)}
              <mark className="transcript-panel__mark">{transcript.slice(span.start, span.end)}</mark>
              {transcript.slice(span.end)}
            </Text>
          </div>
        )}
        <Textarea
          label={t('voice.card.transcriptLabel')}
          value={draft}
          rows={4}
          maxLength={5000}
          disabled={disabled || isReanalysing}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="transcript-panel__actions">
          <Button
            variant="secondary"
            size="sm"
            isLoading={isReanalysing}
            disabled={disabled || draft.trim() === ''}
            onClick={() => onReanalyse(draft.trim())}
          >
            {upgrade ? t('voice.card.analyseWithAi') : t('voice.card.reanalyse')}
          </Button>
          <Text variant="caption" color="muted">
            {reanalyseHint(t, reanalysisCredits, upgrade)}
          </Text>
        </div>
      </div>
    </div>
  );
}

/** Price caption of "Re-analyse" / "Analyse with AI". */
export function reanalyseHint(t: (key: string, opts?: Record<string, unknown>) => unknown, credits: number, upgrade: boolean): string {
  if (upgrade) return String(credits > 0 ? t('voice.card.analyseWithAiPaid', { count: credits }) : t('voice.card.analyseWithAiIncluded'));
  return String(credits > 0 ? t('voice.card.reanalyseHintPaid', { count: credits }) : t('voice.card.reanalyseHint'));
}
